// ============================================================================
// Module 2.0: Ingestion Reporting Service Client
// ============================================================================

import {
  IngestionReportDashboardKPIs,
  IngestionReportForm,
  IngestionReport,
  AnonymousAccessRecord,
  ReportValidationSavePayload,
  ReportValidationApprovePayload,
  ReportValidationRejectPayload,
  AircraftRegistrationRecord,
  ReportRelationAuditRecord,
  ReportAllocationRecord,
  ReportConfidentialityCellRecord,
  ReportClosureTemplateRecord,
  RiskMatrixClosureMapRecord,
  ReportCostRecord,
  ReportAssessmentPayload,
} from './types';
import { apiRequest } from '../../shared/api';

const API_BASE = '/ingestion';

export const ingestionService = {
  // 1. Dashboard KPIs
  async getDashboardKPIs(): Promise<IngestionReportDashboardKPIs> {
    return apiRequest<IngestionReportDashboardKPIs>(`${API_BASE}/analytics/dashboard`);
  },

  async getKpis(): Promise<IngestionReportDashboardKPIs> {
    return this.getDashboardKPIs();
  },

  // 2. Forms Definition & Questions
  async getForms(filters?: { report_category?: string; report_type?: string }): Promise<IngestionReportForm[]> {
    const params = new URLSearchParams();
    if (filters?.report_category) params.append('report_category', filters.report_category);
    if (filters?.report_type) params.append('report_type', filters.report_type);
    const url = params.toString() ? `${API_BASE}/forms?${params.toString()}` : `${API_BASE}/forms`;
    return apiRequest<IngestionReportForm[]>(url);
  },

  async getFormTemplates(filters?: { report_category?: string; report_type?: string }): Promise<IngestionReportForm[]> {
    return this.getForms(filters);
  },

  async getAllReports(): Promise<IngestionReport[]> {
    return this.getReports();
  },

  // 3. Reports Register
  async getReports(filters?: {
    status?: string;
    report_category?: string;
    report_type?: string;
    created_by?: string;
    identity_mode?: string;
    is_draft?: boolean;
  }): Promise<IngestionReport[]> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.report_category) params.append('report_category', filters.report_category);
    if (filters?.report_type) params.append('report_type', filters.report_type);
    if (filters?.created_by) params.append('created_by', filters.created_by);
    if (filters?.identity_mode) params.append('identity_mode', filters.identity_mode);
    if (filters?.is_draft !== undefined) params.append('is_draft', String(filters.is_draft));

    const url = params.toString() ? `${API_BASE}/reports?${params.toString()}` : `${API_BASE}/reports`;
    return apiRequest<IngestionReport[]>(url);
  },

  async getReportById(id: string): Promise<IngestionReport> {
    return apiRequest<IngestionReport>(`${API_BASE}/reports/${id}`);
  },

  // 4. Report Creation / Submission (Wizard)
  async createReport(payload: {
    report_category?: 'SAFETY' | 'NON_SAFETY';
    report_type: string;
    title: string;
    description: string;
    event_date: string;
    event_location?: string;
    airport_code?: string;
    flight_number?: string;
    aircraft_registration?: string;
    department_id?: string;
    initial_severity?: string;
    confidentiality_level?: string;
    identity_mode?: string;
    is_draft?: boolean;
    form_version_id?: string;
    context_data?: Record<string, any>;
    created_by?: string;
    reporter_name?: string;
    reporter_email?: string;
    reporter?: {
      full_name: string;
      email?: string;
      phone?: string;
      job_title?: string;
      department?: string;
    };
  }): Promise<{ report: IngestionReport; anonymousAccess?: AnonymousAccessRecord; tracking_code?: string }> {
    const data = await apiRequest<{ report: IngestionReport; anonymousAccess?: AnonymousAccessRecord }>(
      `${API_BASE}/reports`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      }
    );
    return {
      ...data,
      tracking_code: data?.anonymousAccess?.tracking_code,
    };
  },

  // 4b. Update Report by Reporter (Before SMS Assessment)
  async updateReport(id: string, updates: any): Promise<IngestionReport> {
    const res = await apiRequest<{ success: boolean; data: IngestionReport; message?: string } | IngestionReport>(`${API_BASE}/reports/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    return (res as any)?.data || (res as IngestionReport);
  },

  // 5. State Machine Transition
  async transitionStatus(id: string, status: string, reason?: string): Promise<IngestionReport> {
    return apiRequest<IngestionReport>(`${API_BASE}/reports/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status, reason }),
    });
  },

  async updateReportStatus(id: string, status: string, reason?: string): Promise<IngestionReport> {
    return this.transitionStatus(id, status, reason);
  },

  // 5b. SMS Triage Acceptance, Reclassification (Safety <-> Non-Safety) & Routing
  async triageAndReclassifyReport(
    id: string,
    payload: {
      report_category?: 'SAFETY' | 'NON_SAFETY';
      report_type?: string;
      initial_severity?: string;
      urgency?: string;
      assigned_department?: string;
      assigned_user_id?: string;
      target_status?: string;
      reason?: string;
    }
  ): Promise<IngestionReport> {
    return apiRequest<IngestionReport>(`${API_BASE}/reports/${id}/triage`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },

  // 6. Triage Assignment
  async assignReport(
    id: string,
    assigned_department: string,
    assigned_user_id?: string,
    reclassification?: {
      report_category?: 'SAFETY' | 'NON_SAFETY';
      report_type?: string;
      initial_severity?: string;
      reason?: string;
    }
  ): Promise<IngestionReport> {
    return apiRequest<IngestionReport>(`${API_BASE}/reports/${id}/assign`, {
      method: 'POST',
      body: JSON.stringify({
        assigned_department,
        assigned_user_id,
        ...reclassification,
      }),
    });
  },

  // 7. Delete Draft
  async deleteDraft(id: string): Promise<boolean> {
    return apiRequest<boolean>(`${API_BASE}/reports/${id}/draft`, {
      method: 'DELETE',
    });
  },

  // 8. Update Dynamic Question (Options, Label, Type, Required) - Admin / Form Builder
  async updateQuestion(
    id: string,
    payload: {
      label?: string;
      question_type?: string;
      options_json?: string;
      options?: string[];
      is_required?: boolean;
      help_text?: string;
      section_name?: string;
    }
  ): Promise<any> {
    return apiRequest<any>(`${API_BASE}/forms/questions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },

  // 9. Report Validation Workflow (SCRUM-8)
  async getReportsInValidation(): Promise<IngestionReport[]> {
    return apiRequest<IngestionReport[]>(`${API_BASE}/reports-in-validation`);
  },

  async saveValidationDraft(id: string, payload: ReportValidationSavePayload): Promise<IngestionReport> {
    return apiRequest<IngestionReport>(`${API_BASE}/reports/${id}/validation-save`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },

  async assessReport(
    id: string,
    payload: ReportAssessmentPayload
  ): Promise<{ report: IngestionReport; investigation?: any; action?: any; occurrence?: any; infoReferenceNumber?: string }> {
    return apiRequest<{ report: IngestionReport; investigation?: any; action?: any; occurrence?: any; infoReferenceNumber?: string }>(`${API_BASE}/reports/${id}/assess`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  async mailToReporter(
    id: string,
    payload: {
      recipientEmail?: string;
      subject?: string;
      message: string;
    }
  ): Promise<{ success: boolean; sentTo: string; outboxId?: string }> {
    return apiRequest<{ success: boolean; sentTo: string; outboxId?: string }>(`${API_BASE}/reports/${id}/mail-reporter`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  async getLinkableInfoReports(): Promise<Array<{
    id: string;
    report_number: string;
    info_reference_number?: string;
    title: string;
    event_date: string;
    created_at: string;
    assessment_notes?: string;
  }>> {
    return apiRequest<any[]>(`${API_BASE}/linkable-info`);
  },

  async approveValidation(id: string, payload: ReportValidationApprovePayload): Promise<IngestionReport> {
    return apiRequest<IngestionReport>(`${API_BASE}/reports/${id}/validate-approve`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  async deleteReportWithReason(id: string, reason: string): Promise<IngestionReport> {
    const res = await apiRequest<{ success: boolean; data: IngestionReport } | IngestionReport>(`${API_BASE}/reports/${id}/delete`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
    return (res as any)?.data || (res as any);
  },

  async rejectValidation(id: string, payload: ReportValidationRejectPayload): Promise<IngestionReport> {
    return apiRequest<IngestionReport>(`${API_BASE}/reports/${id}/validate-reject`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  // SafetyManager365 Parity: Aircraft Registrations
  async getAircraftRegistrations(): Promise<AircraftRegistrationRecord[]> {
    return apiRequest<AircraftRegistrationRecord[]>(`${API_BASE}/aircraft-registrations`);
  },

  async createAircraftRegistration(data: { registration: string; aircraft_type: string }): Promise<AircraftRegistrationRecord> {
    return apiRequest<AircraftRegistrationRecord[]>(`${API_BASE}/aircraft-registrations`, {
      method: 'POST',
      body: JSON.stringify(data),
    }) as any;
  },

  // SafetyManager365 Parity: Relate Reports Engine
  async getSuggestedRelations(reportId: string): Promise<IngestionReport[]> {
    return apiRequest<IngestionReport[]>(`${API_BASE}/reports/${reportId}/suggested-relations`);
  },

  async relateReports(masterReportId: string, relatedReportId: string, comment?: string): Promise<{ master: IngestionReport; related: IngestionReport }> {
    return apiRequest<{ master: IngestionReport; related: IngestionReport }>(`${API_BASE}/reports/relate`, {
      method: 'POST',
      body: JSON.stringify({ master_report_id: masterReportId, related_report_id: relatedReportId, comment }),
    });
  },

  async unrelateReport(relatedReportId: string, comment: string): Promise<IngestionReport> {
    return apiRequest<IngestionReport>(`${API_BASE}/reports/${relatedReportId}/unrelate`, {
      method: 'POST',
      body: JSON.stringify({ comment }),
    });
  },

  async getRelatedReports(masterReportId: string): Promise<IngestionReport[]> {
    return apiRequest<IngestionReport[]>(`${API_BASE}/reports/${masterReportId}/related-reports`);
  },

  async getRelationAudit(reportId: string): Promise<ReportRelationAuditRecord[]> {
    return apiRequest<ReportRelationAuditRecord[]>(`${API_BASE}/reports/${reportId}/relations-audit`);
  },

  // SafetyManager365 Parity: Allocations
  async getAllocations(userId?: string, roleId?: string): Promise<ReportAllocationRecord[]> {
    const params = new URLSearchParams();
    if (userId) params.append('user_id', userId);
    if (roleId) params.append('role_id', roleId);
    return apiRequest<ReportAllocationRecord[]>(`${API_BASE}/allocations?${params.toString()}`);
  },

  async saveAllocation(data: Partial<ReportAllocationRecord>): Promise<ReportAllocationRecord> {
    return apiRequest<ReportAllocationRecord>(`${API_BASE}/allocations`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async deleteAllocation(id: string): Promise<{ success: boolean }> {
    return apiRequest<{ success: boolean }>(`${API_BASE}/allocations/${id}`, {
      method: 'DELETE',
    });
  },

  // SafetyManager365 Parity: Confidentiality Matrix
  async getConfidentialityMatrix(roleCode?: string, channel?: string): Promise<ReportConfidentialityCellRecord[]> {
    const params = new URLSearchParams();
    if (roleCode) params.append('role_code', roleCode);
    if (channel) params.append('channel', channel);
    return apiRequest<ReportConfidentialityCellRecord[]>(`${API_BASE}/confidentiality-matrix?${params.toString()}`);
  },

  async updateConfidentialityCell(roleCode: string, channel: string, fieldKey: string, isVisible: boolean): Promise<ReportConfidentialityCellRecord> {
    return apiRequest<ReportConfidentialityCellRecord>(`${API_BASE}/confidentiality-matrix`, {
      method: 'PATCH',
      body: JSON.stringify({ role_code: roleCode, channel, field_key: fieldKey, is_visible: isVisible }),
    });
  },

  // SafetyManager365 Parity: Closure Templates & Strict Rules
  async getClosureTemplates(): Promise<{ templates: ReportClosureTemplateRecord[]; mappings: RiskMatrixClosureMapRecord[] }> {
    return apiRequest<{ templates: ReportClosureTemplateRecord[]; mappings: RiskMatrixClosureMapRecord[] }>(`${API_BASE}/closure-templates`);
  },

  async closeReportWithRules(id: string, overrideReason?: string): Promise<IngestionReport> {
    return apiRequest<IngestionReport>(`${API_BASE}/reports/${id}/close-with-rules`, {
      method: 'POST',
      body: JSON.stringify({ override_reason: overrideReason }),
    });
  },

  // SafetyManager365 Parity: Costs
  async getReportCosts(id: string): Promise<ReportCostRecord | null> {
    return apiRequest<ReportCostRecord | null>(`${API_BASE}/reports/${id}/costs`);
  },

  async saveReportCosts(id: string, data: { direct_cost?: number; indirect_cost?: number; currency?: string; notes?: string }): Promise<ReportCostRecord> {
    return apiRequest<ReportCostRecord>(`${API_BASE}/reports/${id}/costs`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // SafetyManager365 Parity: Linkable Candidates
  async getLinkableCandidates(): Promise<IngestionReport[]> {
    return apiRequest<IngestionReport[]>(`${API_BASE}/linkable-candidates`);
  },

};
